/* eslint-disable @next/next/no-img-element */
import React from 'react';
import DOMPurify from 'isomorphic-dompurify';
import htmlReactParse, { Element } from 'html-react-parser';
import Link from 'next/link';
import { getUserProfileDetail } from 'src/constants/pages';
import { ContentMetadata } from 'src/types/DiscussionPost';
import { QuoteRef } from 'src/types/Quotes';
import QuotesContainer from 'src/components/thread-detail/QuotesContainer';
import Mention from 'src/components/Mention';
import { v4 as uuid } from 'uuid';
import ImageWrapper from 'src/components/image-carousel/ImageWrapper';

const customTags = ['forums-mention', 'forums-quote'];

const transformHtmlToReact = ({
  html, metadata = {}, quoteRef, imageCarousel,
}: {
  html?: string,
  metadata?: ContentMetadata,
  quoteRef?: QuoteRef
  imageCarousel?: {
    images: { id: string, src: string }[],
    displayImage: (imageId: string) => void
  }
}) => {
  if (!html) return null;

  const { mentions = {}, quotes = {} } = metadata;
  const sanitizedHtml = DOMPurify.sanitize(html, { ADD_TAGS: customTags });
  return htmlReactParse(sanitizedHtml, {
    replace: (domNode) => {
      const { name, attribs } = domNode as Element;
      if (name === 'forums-mention' && attribs.id) {
        const mentionId = attribs.id;

        if (!mentions[mentionId]) return <span />;
        const { userId, username } = mentions[mentionId];
        const mentionProps = {
          userId,
          username,
          mentionId,
        };
        const mentionElement = !userId ? <Mention {...mentionProps} disabled /> : (
          <Link prefetch={false} href={getUserProfileDetail(userId)} data-mentions-link>
            <Mention {...mentionProps} />
          </Link>
        );
        return mentionElement;
      }
      if (name === 'forums-quote') {
        const quoteId = attribs.id;
        if (!quotes[quoteId]) return <span />;
        const content: any = transformHtmlToReact({
          html: quotes[quoteId].content,
          metadata,
        });
        return (
          <QuotesContainer
            {...quotes[quoteId]}
            contentJSX={content}
            quoteRef={quoteRef!}
            quoteId={quoteId}
          />
        );
      }
      return null;
    },
    transform: (reactNode: any) => {
      if (reactNode.type === 'img' && imageCarousel) {
        const imageId = uuid();
        imageCarousel.images.push({ src: reactNode.props.src, id: imageId });
        const displayImage = () => imageCarousel.displayImage(imageId);
        return (
          <ImageWrapper
            key={imageId}
            imageElement={reactNode}
            displayImage={displayImage}
          />
        );
      }
      return reactNode;
    },
  });
};

export default transformHtmlToReact;
